import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2, Gauge, Radar, Share2, MapPin, ListChecks, Newspaper, FileSearch, ShieldCheck,
  ThumbsUp, ThumbsDown, AlertTriangle, HelpCircle, Landmark, TrendingUp, TrendingDown, Minus, History,
} from "lucide-react";
import { api } from "@/lib/api";

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const VSTATUS = {
  VERIFIED: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  PUBLIC_SOURCE: { label: "Public source", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  UNVERIFIED: { label: "Unverified", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  ESTIMATED: { label: "Estimated", cls: "text-blue-700 bg-blue-50 border-blue-200" },
  NOT_AVAILABLE: { label: "Not available", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  NOT_FOUND: { label: "Not found", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  NOT_CONFIGURED: { label: "Not configured", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  NONE: { label: "None", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  OK: { label: "Active", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

export function VBadge({ status, testid }) {
  const s = VSTATUS[status] || VSTATUS.NOT_AVAILABLE;
  return <span data-testid={testid} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
}

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-white rounded-xl border border-slate-200 p-6 shadow-sm ${className}`}>{children}</div>;
}

function Head({ icon: Icon, title, sub, right }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-indigo-600" />
        <div>
          <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function EntityCard({ entity }) {
  if (!entity) return null;
  const rows = [
    ["Category", entity.category], ["Industry", entity.industry],
    ["Location", [entity.city, entity.state].filter(Boolean).join(", ")],
    ["Address", entity.address], ["Phone", entity.phone], ["Website", entity.website],
  ].filter(([, v]) => v);
  return (
    <Card testid="bi-entity">
      <Head icon={Building2} title="Business Profile" sub="The entity this report is about" right={<VBadge status={entity.verification_status} testid="bi-entity-status" />} />
      <p className="font-heading text-xl font-semibold text-slate-900">{entity.name}</p>
      <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
            <dt className="text-xs text-slate-500">{k}</dt>
            <dd className="text-xs font-medium text-slate-800 text-right truncate">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-[11px] text-slate-400">Identity source: {entity.source === "google_places" ? "Google Places (verified)" : "AI candidate / user input (unverified)"}.</p>
    </Card>
  );
}

export function DigitalPresenceCard({ digital }) {
  if (!digital) return null;
  return (
    <Card testid="bi-digital-presence">
      <Head icon={Gauge} title="Digital Presence Score" sub={digital.explanation}
        right={<span className="font-heading text-2xl font-bold text-slate-900">{digital.score ?? "—"}<span className="text-sm text-slate-400">/100</span></span>} />
      <div className="space-y-3">
        {(digital.categories || []).map((c) => (
          <div key={c.key}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium text-slate-700">{c.name}</span>
              <span className="text-slate-500">{c.score == null ? "Not measurable" : `${c.score}/100`}</span>
            </div>
            {c.score != null && (
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${c.score}%` }} />
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-1">{c.explanation}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ConnectorStatusGrid({ connectors }) {
  if (!connectors?.length) return null;
  return (
    <Card testid="bi-connectors">
      <Head icon={ShieldCheck} title="Scan Coverage" sub="Which sources were checked. Not-configured sources light up when their API key is added." />
      <div className="grid sm:grid-cols-3 gap-2.5">
        {connectors.map((c) => (
          <div key={c.connector} data-testid={`bi-connector-${c.connector}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-xs font-medium text-slate-700">{c.label}</span>
            <VBadge status={c.connector === "website" && c.verification_status === "NONE" ? "NOT_FOUND" : (c.verification_status || (c.status === "OK" ? "OK" : c.status))} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SocialGrid({ social }) {
  if (!social?.profiles?.length) return null;
  return (
    <Card testid="bi-social">
      <Head icon={Share2} title="Social Media Presence" sub={social.note}
        right={<span className="text-xs text-slate-500">{social.discovered_count || 0} likely official</span>} />
      <div className="grid sm:grid-cols-2 gap-2.5">
        {social.profiles.map((p) => (
          <div key={p.platform} data-testid={`bi-social-${p.platform}`} className="rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-slate-800">{p.platform_label}</span>
              <VBadge status={p.verification_status} />
            </div>
            {p.verification_status === "UNVERIFIED" && p.url ? (
              <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline break-all">{p.url}</a>
            ) : (
              <div className="space-y-0.5">
                {p.note && <p className="text-[11px] text-slate-400">{p.note}</p>}
                {p.candidate_url && (
                  <p className="text-[11px] text-slate-400">Unconfirmed candidate: <a href={p.candidate_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:underline break-all">{p.candidate_url}</a></p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function GoogleBusinessCard({ gbp }) {
  if (!gbp) return null;
  const pb = gbp.data || {};
  return (
    <Card testid="bi-google">
      <Head icon={MapPin} title="Google Business Profile" right={<VBadge status={gbp.verification_status} testid="bi-google-status" />} />
      {gbp.verification_status === "VERIFIED" ? (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {[["Name", pb.name], ["Rating", pb.rating], ["Reviews", pb.review_count], ["Address", pb.address], ["Status", pb.business_status]].filter(([, v]) => v != null).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
              <dt className="text-xs text-slate-500">{k}</dt><dd className="text-xs font-medium text-slate-800">{String(v)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-slate-500">{gbp.message || "Google Business Profile verification unavailable — API not configured."}</p>
      )}
    </Card>
  );
}

export function DirectoriesCard({ directories }) {
  if (!directories?.listings?.length) return null;
  return (
    <Card testid="bi-directories">
      <Head icon={ListChecks} title="Online Directories" sub={directories.note} right={<VBadge status={directories.verification_status} />} />
      <div className="grid sm:grid-cols-2 gap-2">
        {directories.listings.map((d) => (
          <a key={d.source} href={d.lookup_url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:border-indigo-300">
            <span className="text-sm text-slate-700">{d.source}</span>
            <span className="text-[11px] text-indigo-600">Look up →</span>
          </a>
        ))}
      </div>
    </Card>
  );
}

export function WebMentionsCard({ mentions }) {
  if (!mentions) return null;
  return (
    <Card testid="bi-mentions">
      <Head icon={Newspaper} title="News & Media Mentions" right={<VBadge status={mentions.verification_status} />} />
      {mentions.mentions?.length ? (
        <ul className="space-y-3">
          {mentions.mentions.map((m, i) => (
            <li key={i} data-testid={`bi-mention-${i}`} className="border-b border-slate-100 pb-2 last:border-0">
              <a href={m.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-indigo-600 hover:underline">{m.title || m.source}</a>
              {m.snippet && <p className="text-xs text-slate-500 mt-0.5">{m.snippet}</p>}
              <span className="text-[11px] text-slate-400">{m.source}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-500">{mentions.message || "No public news/media mentions found (discovery not configured)."}</p>}
    </Card>
  );
}

function ThemeList({ items, icon: Icon, tone, empty }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 mb-1.5 text-sm font-semibold ${tone}`}><Icon className="w-4 h-4" /> </div>
      {items?.length ? (
        <ul className="space-y-1">
          {items.slice(0, 6).map((t, i) => <li key={i} className="text-sm text-slate-600">• {t}</li>)}
        </ul>
      ) : <p className="text-xs text-slate-400">{empty}</p>}
    </div>
  );
}

export function ReputationThemesCard({ themes }) {
  if (!themes) return null;
  if (!themes.has_data) {
    return (
      <Card testid="bi-reputation-themes">
        <Head icon={ThumbsUp} title="Reputation Themes" sub="AI analysis of collected public reviews & mentions" />
        <p className="text-sm text-slate-500">No verified reviews or public mentions were collected to analyze. Connect Google Places or a Search API to surface reputation themes. <span className="text-slate-400">(We never invent reviews.)</span></p>
      </Card>
    );
  }
  return (
    <Card testid="bi-reputation-themes">
      <Head icon={ThumbsUp} title="Reputation Themes" sub="AI analysis of collected public reviews & mentions — not customer quotes." />
      <div className="grid sm:grid-cols-3 gap-5">
        <div data-testid="bi-themes-positive"><p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> Positive</p><ThemeList items={themes.positive} icon={ThumbsUp} tone="text-emerald-700" empty="None detected." /></div>
        <div data-testid="bi-themes-negative"><p className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5" /> Negative</p><ThemeList items={themes.negative} icon={ThumbsDown} tone="text-rose-700" empty="None detected." /></div>
        <div data-testid="bi-themes-risks"><p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Risks</p><ThemeList items={themes.risks} icon={AlertTriangle} tone="text-amber-700" empty="None detected." /></div>
      </div>
    </Card>
  );
}

export function MissingInfoCard({ missing }) {
  if (!missing?.length) return null;
  return (
    <Card testid="bi-missing">
      <Head icon={HelpCircle} title="Missing Information" sub="Public information we couldn't find — each is a fixable gap." />
      <ul className="space-y-1.5">
        {missing.map((m, i) => (
          <li key={i} data-testid={`bi-missing-${i}`} className="flex items-start gap-2 text-sm text-slate-700">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> {m}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function PublicRecordsCard({ records }) {
  if (!records) return null;
  return (
    <Card testid="bi-public-records">
      <Head icon={Landmark} title="Public Records" right={<VBadge status={records.verification_status} />} />
      {records.records?.length ? (
        <ul className="space-y-2">{records.records.map((r, i) => <li key={i} className="text-sm text-slate-700">{r.title || r.source}</li>)}</ul>
      ) : <p className="text-sm text-slate-500">{records.message || "Public-records lookup not configured."}</p>}
    </Card>
  );
}

function DeltaPill({ delta, invert }) {
  if (delta == null) return <span className="text-xs text-slate-400">—</span>;
  const up = invert ? delta < 0 : delta > 0;
  const flat = delta === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? "text-slate-500" : up ? "text-emerald-600" : "text-rose-600";
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}><Icon className="w-3.5 h-3.5" />{delta > 0 ? `+${delta}` : delta}</span>;
}

export function CompareCard({ scanId }) {
  const [cmp, setCmp] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let ok = true;
    api.get(`/scan/${scanId}/compare`).then(({ data }) => { if (ok) { setCmp(data); setLoaded(true); } })
      .catch(() => { if (ok) setLoaded(true); });
    return () => { ok = false; };
  }, [scanId]);
  if (!loaded || !cmp) return null;
  if (!cmp.has_previous) {
    return (
      <Card testid="bi-compare">
        <Head icon={History} title="Compare Over Time" sub="Run this scan again later to track changes." />
        <p className="text-sm text-slate-500">This is the first saved scan for this business. Future scans will show what changed — new reviews, ratings, profiles, mentions and score movement.</p>
      </Card>
    );
  }
  const d = cmp.diff || {};
  const rows = [
    ["Overall score", d.score],
    ["Digital presence", d.digital_presence],
    ["Google rating", d.rating],
    ["Review count", d.review_count],
  ];
  return (
    <Card testid="bi-compare">
      <Head icon={History} title="Compare Over Time" sub={`vs previous scan · ${(cmp.previous_scanned_at || "").slice(0, 10)}`}
        right={<span className={`text-xs font-semibold uppercase ${d.overall === "improved" ? "text-emerald-600" : d.overall === "declined" ? "text-rose-600" : "text-slate-500"}`}>{d.overall}</span>} />
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {rows.map(([label, v]) => (
          <div key={label} className="flex items-center justify-between border-b border-slate-100 py-1.5">
            <span className="text-sm text-slate-600">{label}</span>
            <span className="flex items-center gap-3 text-sm">
              <span className="text-slate-400">{v?.previous ?? "—"} → <span className="font-medium text-slate-800">{v?.current ?? "—"}</span></span>
              <DeltaPill delta={v?.delta} />
            </span>
          </div>
        ))}
      </div>
      {(d.social_profiles?.new?.length || d.directories?.new?.length || d.news_mentions?.new_count || d.reputation_issues?.new?.length || d.reputation_issues?.resolved?.length) ? (
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
          {d.social_profiles?.new?.length ? <p>New social: {d.social_profiles.new.join(", ")}</p> : null}
          {d.directories?.new?.length ? <p>New directories: {d.directories.new.join(", ")}</p> : null}
          {d.news_mentions?.new_count ? <p>New news mentions: {d.news_mentions.new_count}</p> : null}
          {d.reputation_issues?.resolved?.length ? <p className="text-emerald-700">Resolved issues: {d.reputation_issues.resolved.length}</p> : null}
          {d.reputation_issues?.new?.length ? <p className="text-rose-700">New issues: {d.reputation_issues.new.length}</p> : null}
        </div>
      ) : null}
      {d.compliance && (d.compliance.improved?.length || d.compliance.new_risk?.length || d.compliance.score?.delta != null) ? (
        <div className="mt-3 border-t border-slate-100 pt-3 space-y-1.5" data-testid="bi-compare-compliance">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Compliance score</span>
            <span className="flex items-center gap-3 text-sm">
              <span className="text-slate-400">{d.compliance.score?.previous ?? "—"} → <span className="font-medium text-slate-800">{d.compliance.score?.current ?? "—"}</span></span>
              <DeltaPill delta={d.compliance.score?.delta} />
            </span>
          </div>
          {d.compliance.improved?.length ? (
            <p className="text-xs text-emerald-700 font-medium" data-testid="bi-compliance-improved">✓ Compliance improved: {d.compliance.improved.join(", ")}</p>
          ) : null}
          {d.compliance.new_risk?.length ? (
            <p className="text-xs text-rose-700 font-medium" data-testid="bi-compliance-risk">⚠ New compliance risk detected: {d.compliance.new_risk.join(", ")}</p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function EvidenceTable({ evidence }) {
  if (!evidence?.length) return null;
  return (
    <Card testid="bi-evidence" className="overflow-hidden p-0">
      <div className="p-6 pb-3"><Head icon={FileSearch} title="Evidence & Findings" sub="Every discovered signal, with its source and verification status." /></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr><th className="px-6 py-2.5 font-medium">ID</th><th className="px-6 py-2.5 font-medium">Type</th><th className="px-6 py-2.5 font-medium">Source</th><th className="px-6 py-2.5 font-medium">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {evidence.map((e) => (
              <tr key={e.finding_id} data-testid={`bi-evidence-${e.finding_id}`}>
                <td className="px-6 py-2.5 font-mono text-xs text-slate-500">{e.finding_id}</td>
                <td className="px-6 py-2.5 text-slate-700">{e.type}</td>
                <td className="px-6 py-2.5 text-slate-600 max-w-[240px] truncate">{e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{e.source}</a> : e.source}</td>
                <td className="px-6 py-2.5"><VBadge status={e.verification_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function BusinessIntelDashboard({ report, scanId }) {
  return (
    <motion.div {...fade} className="space-y-6" data-testid="bi-dashboard">
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <EntityCard entity={report.entity} />
        <DigitalPresenceCard digital={report.digital_presence} />
      </div>
      {scanId && <CompareCard scanId={scanId} />}
      <ConnectorStatusGrid connectors={report.connectors} />
      <ReputationThemesCard themes={report.reputation_themes} />
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <SocialGrid social={report.social} />
        <div className="space-y-6">
          <GoogleBusinessCard gbp={report.google_business} />
          <WebMentionsCard mentions={report.web_mentions} />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <DirectoriesCard directories={report.directories} />
        <div className="space-y-6">
          <MissingInfoCard missing={report.missing_information} />
          <PublicRecordsCard records={report.public_records} />
        </div>
      </div>
      <EvidenceTable evidence={report.evidence} />
    </motion.div>
  );
}
